import { api, apiBaseUrl, tokenStore } from './client';
import type { RespuestaPendiente } from './aprobaciones';

export type ItemTipo = 'insumo' | 'compuesto';

export interface InventarioItem {
  id: number;
  codigo: string;
  nombre: string;
  tipo: ItemTipo;
  categoria: string | null;
  unidad: string;
  cantidad: number;
  stock_minimo: number;
  rendimiento: number;
  activo: boolean;
  /** Tipo de empaque (ej. "caja", "bolsa") y cuánto trae cada uno (en `unidad`). */
  empaque_tipo?: string | null;
  empaque_contenido?: number | null;
  stock_updated_at: string | null;
  /** Cantidad comprometida en pedidos abiertos (según recetas). */
  reservado: number;
  /** cantidad − reservado. */
  disponible: number;
}

/** Último costo REAL de compra de un item, con trazabilidad al gasto
 *  (factura) del que salió — GET /api/inventario/costos (ago-2026). */
export interface InventarioCosto {
  item_id: number;
  /** 'compra' = última compra con costo · 'subreceta' = derivado de su receta (oct-2026). */
  origen?: 'compra' | 'subreceta';
  /** Pesos por unidad del item. null = subreceta a la que le falta el costo de algún insumo. */
  costo_unitario: number | null;
  // --- Solo origen 'compra' (en subrecetas vienen null) ---
  costo_linea: number | null;
  cantidad_comprada: number | null;
  gasto_id: number | null;
  gasto_fecha: string | null;   // YYYY-MM-DD
  gasto_proveedor: string | null;
  gasto_factura: string | null;
  // --- Solo origen 'subreceta' ---
  completo?: boolean;
  /** Insumos sin costo que impiden costear la subreceta. */
  faltan?: string[];
  /** Algún insumo de la subreceta tiene un costo implausible. */
  no_creible?: boolean;
  rendimiento?: number;
}

export const listInventarioCostos = (sedeId: number) =>
  api.get<InventarioCosto[]>(`/api/inventario/costos?sede_id=${sedeId}`);

export interface InventarioMovimiento {
  id: number;
  item_id: number;
  item_codigo: string;
  item_nombre: string;
  item_unidad?: string;
  sede_id: number;
  tipo: string;
  cantidad_delta: string;
  saldo_resultante: string;
  referencia_tipo: string | null;
  referencia_id: number | null;
  usuario_id: number | null;
  usuario_nombre: string | null;
  /** Rol de quien hizo el movimiento (para mostrar "Cajero", "Admin"…). */
  usuario_rol?: string | null;
  /** Motivo del ajuste manual (mig. 035): Merma, Vencimiento, Conteo físico… */
  motivo?: string | null;
  /** Si vino de una solicitud de Aprobaciones (mig. 058): quién la aprobó. */
  aprobado_por_nombre?: string | null;
  notas: string | null;
  created_at: string;
}

export interface MovimientosFiltro {
  /** Uno o varios ids separados por coma (multi-select). */
  item_id?: number | string;
  sede_id?: number;
  /** Uno o varios ids separados por coma (multi-select). */
  usuario_id?: number | string;
  tipo?: string;
  desde?: string;
  hasta?: string;
  limit?: number;
}

export interface RecetaLinea {
  id: number;
  item_id: number;
  codigo: string;
  nombre: string;
  tipo: ItemTipo;
  categoria: string | null;
  cantidad: number;
  unidad: string;
  item_unidad: string;
  notas: string | null;
  /** El comensal puede pedir el plato SIN este ingrediente (mig. 033).
   *  No afecta el inventario: es información para cocina. */
  excluible?: boolean;
  /** Precio de la porción extra de este ingrediente EN ESTE PLATO.
   *  null = no se ofrece como adición. Al adicionar sí descuenta stock. */
  precio_adicion?: number | null;
}

export interface DescuentoResultado {
  sede_id: number;
  referencia: string | null;
  descuentos: Array<{
    producto_id: number;
    item_id: number;
    codigo: string;
    nombre: string;
    cantidad_descontada: number;
    unidad: string;
    saldo_resultante: number;
    via: 'compuesto' | 'insumo' | 'sub-receta';
  }>;
  warnings: Array<{ producto_id?: number; item_id?: number; msg: string }>;
}

export const listInventario = (sedeId: number, opts: { tipo?: ItemTipo; categoria?: string; q?: string } = {}) => {
  const p = new URLSearchParams();
  p.set('sede_id', String(sedeId));
  if (opts.tipo) p.set('tipo', opts.tipo);
  if (opts.categoria) p.set('categoria', opts.categoria);
  if (opts.q) p.set('q', opts.q);
  return api.get<InventarioItem[]>(`/api/inventario?${p}`);
};

export const listCategoriasInventario = () => api.get<string[]>('/api/inventario/categorias');

/**
 * Ajuste manual de stock.
 *
 * OJO (ago-2026): si `cantidad_delta` es NEGATIVO, el backend EXIGE `motivo` y
 * una `notas` de al menos 30 caracteres, y responde 400 si falta alguno. La
 * pantalla valida lo mismo antes de enviar, pero la regla vive en el backend:
 * sacar mercancía a mano es la operación con la que se tapa un faltante.
 */
//
// Cajero (oct-2026): si reduce, el backend responde 202 RespuestaPendiente en
// vez de mover el stock. Revisar con `esPendiente()` antes de decir "listo".
export const ajustarStock = (
  item_id: number,
  sede_id: number,
  cantidad_delta: number,
  notas?: string,
  motivo?: string,
) =>
  api.patch<{ ok: true; saldo: number } | RespuestaPendiente>('/api/inventario/stock', { item_id, sede_id, cantidad_delta, notas, motivo });

/** Modo del motivo: entra stock, sale stock, o conteo físico. */
export type MotivoModo = 'in' | 'out' | 'set';

export interface MotivoAjuste {
  id: number;
  nombre: string;
  modo: MotivoModo;
  /** true = viene de fábrica; false = lo creó un usuario. */
  del_sistema: boolean;
}

/** Motivos de ajuste (mig. 035). Antes estaban quemados en el frontend. */
export const listMotivosAjuste = (modo?: MotivoModo) =>
  api.get<{ motivos: MotivoAjuste[]; nota_minima_al_reducir: number }>(
    `/api/inventario/motivos${modo ? `?modo=${modo}` : ''}`,
  );

export const crearMotivoAjuste = (nombre: string, modo: MotivoModo) =>
  api.post<MotivoAjuste & { ya_existia?: boolean }>('/api/inventario/motivos', { nombre, modo });

// El código lo genera el backend (INV-MP-#### / INV-SR-####), no se envía.
export const crearItem = (data: {
  nombre: string; tipo: ItemTipo; categoria?: string; unidad: string; stock_minimo?: number;
  empaque_tipo?: string | null; empaque_contenido?: number | null;
}) => api.post<{ id: number; codigo: string }>('/api/inventario/items', data);

// Cajero (oct-2026): desactivar responde 202 RespuestaPendiente (el resto de
// los cambios del formulario sí se guardan).
export const updateItem = (id: number, data: Partial<{ nombre: string; categoria: string; unidad: string; stock_minimo: number; activo: boolean; empaque_tipo: string | null; empaque_contenido: number | null }>) =>
  api.patch<InventarioItem | (RespuestaPendiente & { item: InventarioItem | null })>(`/api/inventario/items/${id}`, data);

// ---- Cambio de unidad con conversión (oct-2026) ---------------------------
// Cambiar la unidad por `updateItem` ya no se puede (409 'cambio_unidad'):
// solo cambiaba el texto y dejaba el stock y las recetas con números viejos.
export type ModoCambioUnidad = 'convertir' | 'etiqueta';

export interface CambioUnidadResumen {
  item: { id: number; nombre: string; unidad_actual: string; unidad_nueva: string };
  modo: ModoCambioUnidad;
  factor: number;
  compatible: boolean;
  sin_cambio: boolean;
  pierde_precision: boolean;
  stock: Array<{ sede: string; antes: number; despues: number }>;
  stock_minimo: { antes: number; despues: number };
  recetas: { total: number; lineas: Array<{ en: string; en_tipo: 'plato' | 'subreceta' | 'opción'; antes: number; despues: number }> };
  compras: number;
  movimientos: number;
  simulado: boolean;
  aplicado?: boolean;
}

export const simularCambioUnidad = (id: number, unidad: string, modo: ModoCambioUnidad) =>
  api.post<CambioUnidadResumen>(`/api/inventario/items/${id}/unidad`, { unidad, modo, simular: true });

export const cambiarUnidadItem = (id: number, unidad: string, modo: ModoCambioUnidad) =>
  api.post<CambioUnidadResumen>(`/api/inventario/items/${id}/unidad`, { unidad, modo });

/** Item candidato a duplicado devuelto por `buscarItemsSimilares`. */
export interface ItemSimilar {
  id: number;
  codigo: string;
  nombre: string;
  tipo: ItemTipo;
  categoria: string | null;
  unidad: string;
  activo: boolean;
  exacto: boolean;
}

/**
 * ¿Ya existe algo parecido a este nombre? (mig. 041)
 *
 * `exacto`  → mismo nombre normalizado (sin tildes, sin mayúsculas, sin espacios
 *             de sobra). Es EL MISMO item: el backend no deja crearlo.
 * `parecidos` → comparten la primera palabra o uno es prefijo del otro
 *             ("Azúcar" ↔ "Azúcar morena"). Son items distintos y legítimos:
 *             solo se avisa, la creación sigue permitida.
 */
export const buscarItemsSimilares = (nombre: string) =>
  api.get<{ exacto: ItemSimilar | null; parecidos: ItemSimilar[] }>(
    `/api/inventario/items/similares?nombre=${encodeURIComponent(nombre)}`,
  );

export const descontarPedido = (sede_id: number, items: Array<{ producto_id: number; cantidad: number }>, referencia?: string) =>
  api.post<DescuentoResultado>('/api/inventario/descontar', { sede_id, items, referencia });

const movimientosParams = (params: MovimientosFiltro) => {
  const p = new URLSearchParams();
  if (params.item_id) p.set('item_id', String(params.item_id));
  if (params.sede_id) p.set('sede_id', String(params.sede_id));
  if (params.usuario_id) p.set('usuario_id', String(params.usuario_id));
  if (params.tipo) p.set('tipo', params.tipo);
  if (params.desde) p.set('desde', params.desde);
  if (params.hasta) p.set('hasta', params.hasta);
  if (params.limit) p.set('limit', String(params.limit));
  return p;
};

export const listMovimientos = (params: MovimientosFiltro = {}) => {
  const qs = movimientosParams(params).toString();
  return api.get<InventarioMovimiento[]>(`/api/inventario/movimientos${qs ? `?${qs}` : ''}`);
};

/** Descarga el XLSX del reporte de movimientos respetando los filtros activos. */
export async function descargarMovimientos(params: MovimientosFiltro = {}) {
  const qs = movimientosParams(params).toString();
  const token = tokenStore.get();
  const r = await fetch(`${apiBaseUrl}/api/inventario/movimientos/export${qs ? `?${qs}` : ''}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
  const blob = await r.blob();
  const cd = r.headers.get('content-disposition') || '';
  const m = cd.match(/filename="([^"]+)"/);
  const fname = m?.[1] || 'movimientos_inventario.xlsx';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = fname;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// Recetas
export const getRecetaProducto = (productoId: number) =>
  api.get<RecetaLinea[]>(`/api/recetas/producto/${productoId}`);

export const putRecetaProducto = (
  productoId: number,
  lineas: Array<{ item_id: number; cantidad: number; unidad: string; notas?: string; excluible?: boolean; precio_adicion?: number | null }>,
) =>
  api.put<{ ok: true; count: number }>(`/api/recetas/producto/${productoId}`, { lineas });

/** Opciones de personalización de un plato, derivadas de su receta (mig. 033):
 *  qué se puede quitar y qué se puede adicionar, con precio. */
export interface PersonalizacionPlato {
  exclusiones: Array<{ item_id: number; nombre: string; codigo: string }>;
  adiciones: Array<{
    item_id: number; nombre: string; codigo: string;
    precio: number;
    /** Cantidad que se descuenta del stock al adicionar (la de la receta). */
    cantidad: number; unidad: string;
  }>;
}

export const getPersonalizacionPlato = (productoId: number) =>
  api.get<PersonalizacionPlato>(`/api/recetas/producto/${productoId}/personalizacion`);

export const getRecetaCompuesto = (compuestoId: number) =>
  api.get<RecetaLinea[]>(`/api/recetas/compuesto/${compuestoId}`);

export const putRecetaCompuesto = (compuestoId: number, lineas: Array<{ item_id: number; cantidad: number; unidad: string; notas?: string }>) =>
  api.put<{ ok: true; count: number }>(`/api/recetas/compuesto/${compuestoId}`, { lineas });

// --- Cierre mensual de inventario (materias primas + subrecetas) ---
export interface CierreMensualItem {
  id: number;
  codigo: string;
  nombre: string;
  tipo: ItemTipo;
  categoria: string | null;
  unidad: string;
  stock_minimo: number;
  saldo: number;
}
export interface CierreMensualResponse {
  periodo: { anio: number; mes: number; corte: string; parcial: boolean };
  items: CierreMensualItem[];
}

export const getInventarioMensual = (params: { sede_id: number; anio: number; mes: number }) => {
  const qs = new URLSearchParams({ sede_id: String(params.sede_id), anio: String(params.anio), mes: String(params.mes) });
  return api.get<CierreMensualResponse>(`/api/inventario/mensual?${qs.toString()}`);
};

/** Descarga el XLSX del cierre mensual (cierre completo si el mes terminó, parcial si está en curso). */
export async function descargarInventarioMensual(params: { sede_id: number; anio: number; mes: number }) {
  const qs = new URLSearchParams({ sede_id: String(params.sede_id), anio: String(params.anio), mes: String(params.mes) });
  const token = tokenStore.get();
  const r = await fetch(`${apiBaseUrl}/api/inventario/mensual/export?${qs.toString()}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
  const blob = await r.blob();
  const cd = r.headers.get('content-disposition') || '';
  const m = cd.match(/filename="([^"]+)"/);
  const fname = m?.[1] || `inventario_${params.anio}-${String(params.mes).padStart(2, '0')}.xlsx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = fname;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
